import { Div, Label, List, ListItem, P } from '@kizi/core-components';
import { capitalise, TimelineItemProps } from '@kizi/shared-components';
import React, { MouseEventHandler } from 'react';
import { Arrow, Field } from '..';
import { AfterTheSwitch, ProductProsCons } from '../..';
import { COMPARATOR_TYPE } from '../../../funnels';
import {
  combineStrings,
  getIconLabel,
  sortArrayByKey,
} from '../../../utils/global';
import {
  EnergyLabel,
  EnergyProduct,
  getProductProps,
  PriceMode,
} from '../../../utils/product';
import { getLabel } from '../../../utils/translations';
import { ContractSwitchFaq, ContractSwitchFaqItem } from './ContractSwitchFaq';
import { ListComp } from './List';
import { MoreInfoButton } from './MoreInfoButton';
import RatesComp from './Rates';

export function getEnergyLabelItemsList(energy_label: EnergyLabel): Field[] {
  let fields: Field[] = [];

  for (const item in energy_label) {
    const value = energy_label[item];

    if (
      !['percentage_groen', 'radioactief_afval_per_kwh'].includes(item) &&
      value > 0
    ) {
      const label = `${item}`.replace('percentage_', '');

      fields.push({
        icon: getIconLabel(label),
        label: capitalise(label),
        value: `${value}%`,
      });
    }
  }

  return sortArrayByKey(fields, 'value', true);
}

export function getDetailsMap(features, products): Field[] {
  let detailsMap: Field[] = ['Contractduur', 'Tarieven'].map((label) => {
    return { label, value: '' };
  });

  if (Array.isArray(features)) {
    detailsMap[0].value = features[0].value;
    detailsMap[1].value = features[1].value;
  }

  let productsGreen: Field[] = [];

  for (let product of products) {
    productsGreen.push({
      label: `Soort ${getLabel(product.category)}`,
      value: product.filters.green,
    });
  }

  if (products.length === 2) {
    productsGreen.reverse();
  }

  detailsMap = [
    ...detailsMap,
    ...productsGreen,
    {
      label: 'Opzegtermijn',
      value: '1 maand',
    },
  ];

  return detailsMap;
}

export function getPowerSource(energy_label): JSX.Element {
  return energy_label ? (
    <ListComp
      groups={[
        {
          list: getEnergyLabelItemsList(energy_label),
          title: 'Stroomherkomst',
        },
      ]}
    />
  ) : (
    <div />
  );
}

export function getCharacteristics(characteristicsFields): Field[] {
  if (!characteristicsFields) return [];

  const { postcoderoosregeling, ...restFields } = characteristicsFields;

  let characteristicsMap: Field[] = [];

  for (const characteristic in restFields) {
    const characteristicData = restFields[characteristic];

    let values: string[] = [];

    for (const key in characteristicData) {
      if (characteristicData[key] === 'true') {
        values.push(getLabel(key));
      }
    }

    if (values.length) {
      const lastSeparator =
        characteristic === 'annual_account' ? 'of' : undefined;
      characteristicsMap.push({
        label: capitalise(getLabel(characteristic)),
        value: capitalise(combineStrings(values, lastSeparator)),
      });
    }
  }

  characteristicsMap.push({
    label: capitalise(getLabel('postcoderoosregeling')),
    value: getLabel(postcoderoosregeling) || 'Onbekend',
  });

  return characteristicsMap;
}

export function getContractSwitchFaqMockData(
  name,
  switchWindowDays,
  type,
  taskDivisions,
): ContractSwitchFaqItem[] {
  if (type === 'energyAuction') {
    return [
      {
        label:
          'Moet je lid zijn of worden om mee te doen met het Energiecollectief?',
        content: `Ja. Ben je nog geen lid, dan heten we je welkom met ons Basislidmaatschap. Je eerste 3 maanden zijn gratis. Daarna betaal je €3.25 per maand (maandelijks opzegbaar). Met je Basislidmaatschap bespaar je makkelijk je vaste lasten. `,
      },
      {
        label: `Ben ik met dit aanbod voordeliger uit?`,
        content: `Dat weten we niet. Mogelijk zijn je huidige vaste of variabele tarieven goedkoper. Of is er over een tijdje een beter aanbod. Vergelijk daarom altijd je huidige tarieven met de tarieven die wij nu aanbieden.`,
      },
      {
        label: `Wanneer komt er een nieuw aanbod?`,
        content: `Dat weten we nog niet. We doen ons best voor een goede deal, maar het blijft lastig met de huidige energiemarkt om te voorspellen wanneer we weer een nieuw aanbod kunnen regelen.`,
      },
      {
        label: `Wat is het kijkvenster en hoe werkt dit?`,
        content: `Het kijkvenster van Budget Energie is 6 maanden Loopt jouw huidige contract af binnen dit kijkvenster dan kun je kosteloos overstappen. Heb je een einddatum dat buiten het kijkvenster valt dan is het mogelijk dat jouw huidige leverancier een opzegboete in rekening brengt. Jouw nieuwe leverancier neemt deze opzegboete niet over. Wil je gebruik maken als bestaande klant van het aanbod van je huidige leverancier via het collectief, dat kan alleen als jouw einddatum van jouw contract binnen het kijkvenster valt.`,
      },
    ];
  } else if (type === 'solarPanels' && taskDivisions?.length >= 3) {
    return [
      {
        label: 'Wat is een technische schouw?',
        content: taskDivisions[0]?.value || '',
      },
      {
        label: 'Kan mijn prijs nog veranderen?',
        content: taskDivisions[1]?.value || '',
      },
      {
        label: 'Wanneer moet ik betalen?',
        content: taskDivisions[2]?.value || '',
      },
    ];
  } else {
    return [
      {
        label: 'Ik ben nog nooit overgestapt en heb nooit verlengd',
        content: `Ben je nooit overgestapt en heb je nooit je contract verlengd? Dan stap je zonder problemen over. ${name} zegt je contract direct op en houdt rekening met de opzegtermijn van je huidige leverancier.`,
      },
      {
        label: `Mijn huidige contract loopt binnen ${switchWindowDays} dagen af`,
        content: `Loopt je contract binnen nu en ${switchWindowDays} dagen af? Dan kun je overstappen naar ${name} per de einddatum van je contract. Zo voorkom je een eventuele opzegvergoeding.`,
      },
      {
        label: `Mijn huidige contract loopt nog langer dan ${switchWindowDays} dagen`,
        content: `Loopt je contract nog langer dan ${switchWindowDays} dagen? Neem dan contact met ons op om te bekijken of overstappen wel loont. Want in dit geval zegt ${name} je contract op en stap je direct over. Je huidige leverancier kan dan een opzegvergoeding in rekening brengen. Ook kun je een eventuele welkomstkorting van je huidige leverancier mislopen. Neem ajb contact met ons op.`,
      },
    ];
  }
}

export function getMockTimelineItems(
  type: COMPARATOR_TYPE | string,
  name: string,
): TimelineItemProps[] {
  if (type === COMPARATOR_TYPE.ENERGY) {
    return [
      {
        title: 'Bevestiging Consumentenbond',
        body: 'Je krijgt van ons direct een bevestiging van je aanvraag. En de details van je contract.',
      },
      {
        title: 'Bevestiging leverancier',
        body: `Binnen twee dagen krijg je een welkomstbericht van ${name}. Vanaf dan heb je 14 dagen bedenktijd.`,
      },
      {
        title: 'Meterstanden',
        body: `${name} zegt je huidige contract op en laat weten wanneer je nieuwe contract ingaat. Ook vraagt ${name} naar je meterstanden.`,
      },
      {
        title: 'Besparing geregeld!',
        body: `Zodra ${name} begint met leveren, begin jij met besparen.`,
      },
    ];
  } else if (type === 'energyAuction') {
    return [
      {
        title: 'Bevestiging Consumentenbond',
        body: 'Je krijgt van ons een e-mailbevestiging van je aanvraag',
      },
      {
        title: `Bevestiging leverancier`,
        body: `Binnen 7 dagen krijg je een e-mailbevestiging van je nieuwe energieleverancier ${name}. Vanaf dan heb je 14 dagen bedenktijd. `,
      },
      {
        title: 'Meterstanden',
        body: `${name} zegt je huidige contract op en laat je weten op welke datum je nieuwe contract start. Rond die datum checkt ${name} bij jou de meterstanden.`,
      },
      {
        title: 'Overstap geregeld',
        body: `${name} start op de bevestigde datum met energie leveren. Je overstap via ons Energiecollectief  is geregeld.`,
      },
    ];
  } else {
    return [
      {
        title: 'Bevestiging Consumentenbond',
        body: 'Je krijgt van ons direct een bevestiging van je aanvraag. En de details van je contract.',
      },
      {
        title: `Bevestiging ${name}`,
        body: `Binnen 5 minuten krijg je van ${name} per e-mail bericht of je bent verzekerd.`,
      },
      {
        title: 'Opzeggen verzekering',
        body: `Zeg zelf je (eventuele) huidige verzekering op. Je kunt hiervoor gebruik maken van onze gratis opzegservice. Zo voorkom je dat je dubbel verzekerd bent.`,
      },
    ];
  }
}

export const accordionItemLabel = (label: string) => (isOpen: boolean) => {
  const labelClassName = isOpen ? 'selected' : '';
  return (
    <P display="flex" justifyContent="space-between">
      <Label className={labelClassName}>{label}</Label>
      <Arrow isOpen={isOpen} />
    </P>
  );
};

export function getExpandedData(
  data: EnergyProduct,
  priceMode: PriceMode,
  setModalTab0: MouseEventHandler,
  setModalTab1: MouseEventHandler,
  setModalTab2: MouseEventHandler,
) {
  const { type } = data;
  const purchasableProduct =
    (data as any)?.availability?.acceptance?.accepted !== false;

  const {
    attributes,
    energy_label,
    features,
    name,
    price,
    products,
    providerDescription,
  } = getProductProps(data, priceMode);

  const {
    postcoderoosregeling,
    annual_account,
    edit_amount,
    meter_readings,
    payment_method,
    usage,
    prosAndCons,
    switchWindow,
  } = attributes;

  const isArrayOfProsAndCons = Array.isArray(prosAndCons);
  const _prosAndCons = isArrayOfProsAndCons ? prosAndCons[0] : prosAndCons;

  const mockTimelineItems = type ? getMockTimelineItems(type, name) : [];

  const switchWindowDays = switchWindow ? switchWindow.maxDays : 0;
  const result = [
    {
      label: accordionItemLabel('Over leverancier'),
      content: (
        <>
          <Div display={[null, null, 'flex']}>
            <Div>
              <List variant="accordionProductInner">
                <ListItem>
                  {name && (
                    <Div variant="detailsListHeading">
                      <P variant="bodyImportant">{`Over ${name}`}</P>
                    </Div>
                  )}
                  <p>{providerDescription}</p>
                </ListItem>
                <ListItem>
                  <ProductProsCons {..._prosAndCons} />
                </ListItem>
              </List>
            </Div>
            <ListComp
              groups={[
                {
                  rightFieldStyle: {
                    textAlign: 'left',
                  },
                  list: getCharacteristics({
                    payment_method,
                    annual_account,
                    meter_readings,
                    edit_amount,
                    usage,
                    postcoderoosregeling,
                  }),
                  title: 'Kenmerken',
                },
              ]}
            />
          </Div>
          <MoreInfoButton
            onClick={setModalTab0}
            text="Alles over deze leverancier"
          />
        </>
      ),
    },
    {
      label: accordionItemLabel('Productdetails'),
      content: (
        <>
          <Div display={[null, null, 'flex']}>
            <ListComp
              groups={[
                {
                  list: getDetailsMap(features, products),
                  title: name,
                },
              ]}
            />
            {getPowerSource(energy_label)}
          </Div>
          <MoreInfoButton
            onClick={setModalTab1}
            text="Toon alle productdetails"
          />
        </>
      ),
    },
    {
      label: accordionItemLabel('Tarieven'),
      content: (
        <RatesComp
          price={price}
          type={type}
          data={data}
          onButtonInfoClick={setModalTab2}
        />
      ),
    },
  ];

  if (purchasableProduct) {
    result.push({
      label: accordionItemLabel('Over je aanvraag'),
      content: (
        <Div display={[null, null, 'flex']} variant="productTabSwitch">
          <Div className="tab-section">
            <P variant="bodyImportant">{`Overstappen naar ${name}`}</P>
            <P variant="footnoteRegular">
              {`Als je overstapt, zegt ${name} het contract bij je huidige
              energieleverancier op.`}
            </P>
            <ContractSwitchFaq
              items={getContractSwitchFaqMockData(
                name,
                switchWindowDays,
                type,
                null,
              )}
            />
          </Div>
          <AfterTheSwitch
            title="Zo werkt je aanvraag"
            subtitle="Beste deal gevonden? Hieronder lees je wat je kunt verwachten na de aanvraag."
            steps={mockTimelineItems}
          />
        </Div>
      ),
    });
  }

  return result;
}
